import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { api, type Comune, type Via, type Civico, type CoverageResult, type CoverageLinea } from '@/lib/api'
import { Icons } from './Icons'
import { CoverageVerifying, COVERAGE_VERIFY_MIN_MS } from './CoverageVerifying'
import { useCoverageHealth } from '@/hooks/useCoverageHealth'
import { writeCoverage, coverageFromResult, lineaDistanceNote } from '@/lib/checkout'
import { trackEvent } from '@/lib/analytics'
import { segnaInvioForm } from '@/lib/pageTracking'
import { getRecaptchaToken } from '@/lib/recaptcha'

type Step = 'comune' | 'via' | 'civico'

// Prima linea attivabile = esito "coperta" (stessa logica della modale esito).
const primaAttivabile = (r: CoverageResult) => r.linee.find((l) => l.stato === 'Attivabile')

export function CoverageWizard({ fonte = 'home' }: { fonte?: string } = {}) {
  const [comune, setComune] = useState<Comune | null>(null)
  const [via, setVia] = useState<Via | null>(null)
  const [civico, setCivico] = useState<Civico | null>(null)
  const [verifyingAddr, setVerifyingAddr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CoverageResult | null>(null)
  const [rescue, setRescue] = useState(false)
  const health = useCoverageHealth()
  const navigate = useNavigate()
  const verifying = verifyingAddr !== null

  // Salva la copertura nel bridge e porta al funnel /verifica-copertura.
  const goToFunnel = () => {
    if (comune && via && civico && result) {
      writeCoverage(coverageFromResult(result, comune, via, civico))
    }
    if (result) {
      trackEvent('copertura_continua', { fonte, esito: primaAttivabile(result) ? 'coperta' : 'non_coperta' })
    }
    navigate('/verifica-copertura')
  }

  const step: Step = !comune ? 'comune' : !via ? 'via' : 'civico'
  const canSubmit = !!(comune && via && civico)
  const submitRef = useRef<HTMLButtonElement>(null)

  // Indirizzo completo → focus sul bottone Verifica: Enter avvia la verifica senza mouse.
  useEffect(() => { if (canSubmit) submitRef.current?.focus() }, [canSubmit])

  async function handleVerify() {
    if (!canSubmit) return
    setError(null)
    setVerifyingAddr(`${via!.nome} ${civico!.numero}, ${comune!.nome}`)
    try {
      // Overlay sopra la fetch reale, con attesa minima garantita (CoverageVerifying).
      const [r] = await Promise.all([
        api.verifyCoverage({ comune: comune!, via: via!, civico: civico! }),
        new Promise((res) => setTimeout(res, COVERAGE_VERIFY_MIN_MS)),
      ])
      setResult(r)
      const attiva = primaAttivabile(r)
      trackEvent('verifica_copertura', {
        esito: attiva ? 'coperta' : 'non_coperta',
        tecnologia: attiva?.tecnologia ?? '',
        comune: comune!.nome,
        fonte,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore sconosciuto')
      trackEvent('verifica_copertura', { esito: 'errore', comune: comune!.nome, fonte })
    } finally {
      setVerifyingAddr(null)
    }
  }

  function reset() {
    setComune(null); setVia(null); setCivico(null); setResult(null); setError(null)
  }

  return (
    <section className="coverage" data-sezione="verifica-copertura">
      <div className="coverage-inner">
        <div>
          <h2>Controlla la <span className="g">copertura</span> a casa tua.</h2>
          <p>30 secondi. Nessuna registrazione. Ti diciamo subito la tecnologia migliore disponibile al tuo indirizzo.</p>
        </div>
        <form
          className="coverage-form cov-wizard"
          onSubmit={(e) => { e.preventDefault(); handleVerify() }}
        >
          {(comune || via || civico) && (
            <div className="cov-crumbs">
              {comune && (
                <button type="button" className="cov-crumb" onClick={() => { setComune(null); setVia(null); setCivico(null) }}>
                  <span className="cov-crumb-label">Comune</span>
                  <span className="cov-crumb-val">{comune.nome}</span>
                  <span className="cov-crumb-x">×</span>
                </button>
              )}
              {via && (
                <button type="button" className="cov-crumb" onClick={() => { setVia(null); setCivico(null) }}>
                  <span className="cov-crumb-label">Via</span>
                  <span className="cov-crumb-val">{via.nome}</span>
                  <span className="cov-crumb-x">×</span>
                </button>
              )}
              {civico && (
                <button type="button" className="cov-crumb" onClick={() => setCivico(null)}>
                  <span className="cov-crumb-label">Civico</span>
                  <span className="cov-crumb-val">{civico.numero}</span>
                  <span className="cov-crumb-x">×</span>
                </button>
              )}
            </div>
          )}

          <div className="cov-step-row">
            {step === 'comune' && (
              <CovSelect<Comune>
                key="step-comune"
                placeholder="Inserisci il tuo comune"
                fetch={api.searchComuni}
                minChars={2}
                getLabel={(c) => c.provincia ? `${c.nome} (${c.provincia})` : c.nome}
                onSelect={setComune}
                autoFocus
                disabled={health === 'unavailable'}
                track={{ campo: 'comune', fonte }}
                onNotFound={() => setRescue(true)}
              />
            )}
            {step === 'via' && comune && (
              <CovSelect<Via>
                key="step-via"
                placeholder="Via o piazza"
                fetch={(q) => api.searchVie(comune.egonId, q)}
                minChars={3}
                getLabel={(v) => v.frazione ? `${v.nome} — ${v.frazione}` : v.nome}
                onSelect={setVia}
                autoFocus
                track={{ campo: 'via', fonte }}
                onNotFound={() => setRescue(true)}
              />
            )}
            {step === 'civico' && via && !civico && (
              <CovSelect<Civico>
                key="step-civico"
                placeholder="Numero civico"
                fetch={(q) => api.searchCivici(via.egonId, q)}
                minChars={1}
                getLabel={(c) => c.numero}
                onSelect={setCivico}
                autoFocus
                track={{ campo: 'civico', fonte }}
                onNotFound={() => setRescue(true)}
              />
            )}
            {canSubmit && (
              <div className="cov-ready">
                <Icons.check style={{ width: 18, height: 18, color: 'var(--sh-cyan)' }} />
                <span>Indirizzo completo — premi Verifica</span>
              </div>
            )}
            <button ref={submitRef} className="cta-grad" type="submit" disabled={!canSubmit || verifying || health === 'unavailable'}>
              {verifying ? 'Verifica…' : 'Verifica ora'} <Icons.arrow />
            </button>
          </div>

          {health === 'unavailable' && (
            <p className="cov-unavailable-msg">Verifica di copertura al momento non disponibile</p>
          )}
          {error && (
            <p style={{ color: 'var(--sh-magenta)', fontSize: 13, marginTop: 4 }}>⚠️ {error}</p>
          )}
        </form>
      </div>

      <CoverageVerifying target={verifyingAddr ? { address: verifyingAddr } : null} />

      {result && (
        <ResultModal
          result={result}
          address={`${via?.nome} ${civico?.numero}, ${comune?.nome}`}
          onClose={() => setResult(null)}
          onReset={reset}
          onChoose={goToFunnel}
        />
      )}
      {rescue && (
        <AddressRescueModal
          contesto={`Indirizzo non trovato (widget ${fonte}): ${[comune?.nome, via?.nome].filter(Boolean).join(', ') || 'nessuna selezione'}`}
          onClose={() => setRescue(false)}
        />
      )}
    </section>
  )
}

// ============================================================
// CovSelect — autocomplete con dropdown stile design
// (esportato perche' usato anche da HeroCoverage in /offerte-casa)
// ============================================================
export interface CovSelectProps<T> {
  placeholder: string
  fetch: (q: string) => Promise<T[]>
  minChars: number
  getLabel: (item: T) => string
  onSelect: (item: T) => void
  autoFocus?: boolean
  disabled?: boolean
  /** Se presente, traccia in GA4 le azioni sul campo (evento indirizzo_campo). */
  track?: { campo: 'comune' | 'via' | 'civico'; fonte: string }
  /** Via d'uscita sui vicoli ciechi ("nessun risultato" / errore API): apre il recupero contatto. */
  onNotFound?: () => void
}

export function CovSelect<T>({ placeholder, fetch, minChars, getLabel, onSelect, autoFocus, disabled, track, onNotFound }: CovSelectProps<T>) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<T[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [active, setActive] = useState(0)
  // Un solo evento per "streak" di vicolo cieco: senza questo flag ogni carattere
  // digitato su una query senza risultati genererebbe un evento GA4.
  const deadEndTracked = useRef(false)
  // Dipendenze primitive: track è un oggetto inline ai call site (identità nuova a
  // ogni render), le due stringhe invece sono stabili e non fanno churn sull'effect.
  const trackCampo = track?.campo
  const trackFonte = track?.fonte
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()

  useEffect(() => {
    const h = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || popupRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  // Il dropdown è portalato su <body> e posizionato in fixed sull'input: altrimenti
  // verrebbe tagliato dagli antenati con overflow:hidden (.page-hero) o ridotto al
  // box del widget dal backdrop-filter di .hero-cov (containing block per i fixed).
  useLayoutEffect(() => {
    if (!open || disabled) return
    const update = () => {
      const r = wrapRef.current?.getBoundingClientRect()
      if (r) setCoords({ top: r.bottom + 8, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, disabled])

  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      inputRef.current.focus({ preventScroll: true })
      setOpen(true)
    }
  }, [autoFocus, disabled])

  useEffect(() => {
    if (query.length < minChars) {
      setResults([])
      setFetchError(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(query)
        if (!cancelled) {
          setResults(r)
          setFetchError(false)
          setOpen(true)
          if (r.length > 0) deadEndTracked.current = false
          else if (trackCampo && !deadEndTracked.current) {
            deadEndTracked.current = true
            trackEvent('indirizzo_campo', { campo: trackCampo, azione: 'nessun_risultato', fonte: trackFonte ?? '' })
          }
        }
      } catch {
        // Un guasto della ricerca NON è "nessun risultato": distinguere i due stati
        // evita il falso negativo "il tuo indirizzo non esiste" quando è l'API a soffrire.
        if (!cancelled) {
          setResults([])
          setFetchError(true)
          if (trackCampo && !deadEndTracked.current) {
            deadEndTracked.current = true
            trackEvent('indirizzo_campo', { campo: trackCampo, azione: 'errore_api', fonte: trackFonte ?? '' })
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [query, fetch, minChars, trackCampo, trackFonte])

  const shouldShow = query.length >= minChars
  const visible = results.slice(0, 8)

  // Nuovi risultati → riparti dall'evidenziazione sul primo (Enter seleziona il match migliore).
  useEffect(() => { setActive(0) }, [results])

  // Tieni l'opzione attiva sempre visibile quando si naviga da tastiera (il dropdown scrolla).
  useEffect(() => {
    if (!open) return
    popupRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  const choose = (item: T) => {
    if (track) trackEvent('indirizzo_campo', { campo: track.campo, azione: 'selezionato', fonte: track.fonte })
    deadEndTracked.current = false
    onSelect(item)
    setQuery('')
    setOpen(false)
    setActive(0)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) { setOpen(true); break }
        if (visible.length) setActive((a) => (a + 1) % visible.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (!open) { setOpen(true); break }
        if (visible.length) setActive((a) => (a - 1 + visible.length) % visible.length)
        break
      case 'Enter':
        // Solo se c'è un'opzione evidenziata: seleziona e NON far submittare il form (Verifica).
        if (open && shouldShow && !loading && visible[active]) {
          e.preventDefault()
          choose(visible[active])
        }
        break
      case 'Escape':
        if (open) { e.preventDefault(); setOpen(false) }
        break
    }
  }

  return (
    <div className={`cov-select ${open ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`} ref={wrapRef}>
      {/* Tutto il box è superficie di click (il padding del ctrl non deve "mangiare" il focus). */}
      <div className="cov-select-ctrl" onClick={() => { if (!disabled) { inputRef.current?.focus(); setOpen(true) } }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          role="combobox"
          aria-expanded={open && !disabled}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && shouldShow && !loading && visible[active] ? `${listboxId}-opt-${active}` : undefined}
          autoComplete="off"
          onFocus={() => !disabled && setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onKeyDown={handleKeyDown}
        />
        <span className="cov-caret">▾</span>
      </div>
      {open && !disabled && coords && createPortal(
        <div
          ref={popupRef}
          className="cov-dropdown"
          id={listboxId}
          // role="listbox" SOLO quando ci sono opzioni reali: un listbox vuoto (stati
          // "Digita almeno…", "Caricamento…", "Nessun risultato") viola la regola ARIA
          // "il listbox deve contenere option" (PageSpeed/axe). Senza opzioni resta un div.
          role={shouldShow && !loading && visible.length > 0 ? 'listbox' : undefined}
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, right: 'auto' }}
        >
          {!shouldShow && <div className="cov-hint">Digita almeno {minChars} caratteri…</div>}
          {shouldShow && loading && <div className="cov-hint">Caricamento…</div>}
          {shouldShow && !loading && fetchError && (
            <div className="cov-hint">La ricerca indirizzi ha un problema temporaneo: riprova tra qualche istante.</div>
          )}
          {shouldShow && !loading && !fetchError && results.length === 0 && (
            <div className="cov-hint">Nessun risultato</div>
          )}
          {shouldShow && !loading && (fetchError || results.length === 0) && onNotFound && (
            <button
              type="button"
              className="cov-hint"
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 0, cursor: 'pointer', textDecoration: 'underline', font: 'inherit', color: 'inherit' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setOpen(false); onNotFound() }}
            >
              Non trovi il tuo indirizzo? Lasciaci il numero: controlliamo noi →
            </button>
          )}
          {shouldShow && !loading && visible.map((item, idx) => (
            <div
              key={idx}
              id={`${listboxId}-opt-${idx}`}
              role="option"
              aria-selected={idx === active}
              data-idx={idx}
              className={`cov-opt ${idx === active ? 'is-sel' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(idx)}
              onClick={() => choose(item)}
            >
              <span className="cov-opt-label">{highlightMatch(getLabel(item), query)}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

function highlightMatch(text: string, q: string) {
  if (!q) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  )
}

// ============================================================
// Result modal — una sola linea (la prima = la più performante)
// ============================================================
function ResultModal({ result, address, onClose, onReset, onChoose }: {
  result: CoverageResult
  address: string
  onClose: () => void
  onReset: () => void
  onChoose: () => void
}) {
  // Il backend restituisce le linee già ordinate con la MIGLIORE (massima velocità
  // raggiungibile all'indirizzo) in cima: mostriamo quella, senza riordinare/ricalcolare
  // lato client. find() per stato = la prima attivabile (o, se non coperto, la prima probabile).
  const linea: CoverageLinea | undefined = result.linee.find((l) => l.stato === 'Attivabile')
  const probable: CoverageLinea | undefined = result.linee.find((l) => l.stato === 'Probabile')

  return (
    <div className="cov-modal-backdrop" onClick={onClose}>
      <div className={`cov-modal ${linea ? `tier-${tierOf(linea)}` : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button type="button" className="cov-modal-close" onClick={onClose} aria-label="Chiudi">×</button>

        {linea ? <CoveredView linea={linea} address={address} onReset={onReset} onChoose={onChoose} /> : <NotCoveredView probable={probable} address={address} onReset={onReset} onChoose={onChoose} />}
      </div>
    </div>
  )
}

function CoveredView({ linea, address, onReset, onChoose }: { linea: CoverageLinea; address: string; onReset: () => void; onChoose: () => void }) {
  const icon = linea.tecnologia === 'FWA' ? <Icons.signal style={{ width: 32, height: 32 }} /> : <Icons.bolt style={{ width: 32, height: 32 }} />
  return (
    <>
      <div className="cov-modal-icon">{icon}</div>
      <div className="cov-modal-eyebrow">Buone notizie</div>
      <h3 className="cov-modal-title">
        Disponibile: <span className="g">{linea.tecnologia} {formatSpeed(linea.velocitaDownloadMbps)}</span>
      </h3>
      <div className="cov-modal-addr">{address}</div>
      {lineaDistanceNote(linea) && <div className="cov-modal-dist">{lineaDistanceNote(linea)}</div>}
      <div className="cov-modal-specs">
        <Spec k="Download" v={formatSpeed(linea.velocitaDownloadMbps)} />
        <Spec k="Upload" v={formatSpeed(linea.velocitaUploadMbps)} />
      </div>
      <div className="cov-modal-actions">
        <button className="cta-grad" type="button" onClick={onChoose}>Scegli offerta <Icons.arrow /></button>
        <button className="cta-ghost" type="button" onClick={onReset}>Cambia indirizzo</button>
      </div>
    </>
  )
}

function NotCoveredView({ probable, address, onReset, onChoose }: { probable?: CoverageLinea; address: string; onReset: () => void; onChoose: () => void }) {
  return (
    <>
      <div className="cov-modal-icon"><Icons.pin style={{ width: 32, height: 32 }} /></div>
      <div className="cov-modal-eyebrow">Non ancora coperto</div>
      <h3 className="cov-modal-title">
        {probable
          ? <>In arrivo: <span className="g">{probable.tecnologia}</span></>
          : <>L'indirizzo <span className="g">non è ancora coperto</span></>}
      </h3>
      <div className="cov-modal-addr">{address}</div>
      <p style={{ marginTop: 8, color: 'rgba(255,255,255,0.7)' }}>
        {probable
          ? 'Lasciaci un contatto e ti avvisiamo appena la attiviamo.'
          : 'Lasciaci un contatto e ti avvisiamo appena portiamo la fibra al tuo indirizzo.'}
      </p>
      <div className="cov-modal-actions">
        <button className="cta-grad" type="button" onClick={onChoose}>Lascia un contatto <Icons.arrow /></button>
        <button className="cta-ghost" type="button" onClick={onReset}>Cambia indirizzo</button>
      </div>
    </>
  )
}

function Spec({ k, v }: { k: string; v: string }) {
  return (
    <div className="cov-spec">
      <div className="cov-spec-k">{k}</div>
      <div className="cov-spec-v">{v}</div>
    </div>
  )
}

function formatSpeed(mbps: number): string {
  if (mbps >= 1000) {
    const g = mbps / 1000
    return `${Number.isInteger(g) ? g : g.toFixed(1).replace('.', ',')} Gbps`
  }
  return `${mbps} Mbps`
}

function tierOf(linea: CoverageLinea): 'top' | 'good' | 'ok' {
  if (linea.tecnologia === 'FTTH' && linea.velocitaDownloadMbps >= 2000) return 'top'
  if (linea.tecnologia === 'FTTH') return 'good'
  return 'ok'
}

// ============================================================
// AddressRescueModal — via d'uscita dai vicoli ciechi dell'indirizzo
// (civico nuovo, via mancante in banca dati Egon, errore API): invece
// di perdere il cliente su "Nessun risultato" raccogliamo un contatto
// e verifichiamo noi la copertura. Riusato da wizard, hero e funnel.
// ============================================================
const rescueInputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px', borderRadius: 10, fontSize: 15,
  border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.08)', color: '#fff',
}

export function AddressRescueModal({ contesto, onClose }: { contesto: string; onClose: () => void }) {
  const [nome, setNome] = useState('')
  const [telefono, setTelefono] = useState('')
  const [privacy, setPrivacy] = useState(false)
  const [phase, setPhase] = useState<'form' | 'sending' | 'ok'>('form')
  const [err, setErr] = useState<string | null>(null)
  const FONTE = 'indirizzo-non-trovato'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nome.trim() || !telefono.trim()) { setErr('Inserisci nome e telefono.'); return }
    if (!privacy) { setErr("Per procedere accetta l'informativa privacy."); return }
    setErr(null)
    setPhase('sending')
    try {
      const recaptchaToken = await getRecaptchaToken('lead')
      await api.submitLead({
        tipo: 'Richiamo',
        nome: nome.trim(),
        telefono: telefono.trim(),
        messaggio: contesto,
        consensoPrivacy: true,
        fonte: FONTE,
        recaptchaToken,
      })
      trackEvent('lead_inviato', { fonte: FONTE })
      segnaInvioForm()
      setPhase('ok')
    } catch {
      setPhase('form')
      setErr('Invio non riuscito. Riprova tra poco o chiamaci allo 0544 2330899.')
    }
  }

  // Portalata su <body>: dentro widget con backdrop-filter (hero-cov) lo stacking
  // context intrappolerebbe il backdrop fixed (stesso motivo di HeroResultModal).
  return createPortal(
    <div className="cov-modal-backdrop" onClick={onClose}>
      <div className="cov-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button type="button" className="cov-modal-close" onClick={onClose} aria-label="Chiudi">×</button>
        <div className="cov-modal-icon"><Icons.pin style={{ width: 32, height: 32 }} /></div>
        <div className="cov-modal-eyebrow">Non trovi il tuo indirizzo?</div>
        {phase === 'ok' ? (
          <>
            <h3 className="cov-modal-title">Richiesta <span className="g">ricevuta</span></h3>
            <p style={{ margin: '8px 0 16px', color: 'rgba(255,255,255,0.7)' }}>
              Controlliamo noi la copertura al tuo indirizzo e ti richiamiamo con l'esito. A presto!
            </p>
            <div className="cov-modal-actions">
              <button className="cta-grad" type="button" onClick={onClose}>Chiudi</button>
            </div>
          </>
        ) : (
          <>
            <h3 className="cov-modal-title">Controlliamo <span className="g">noi per te</span></h3>
            <p style={{ margin: '8px 0 14px', color: 'rgba(255,255,255,0.7)' }}>
              Succede con i civici nuovi o le vie appena aggiornate in banca dati. Lasciaci nome e
              numero: verifichiamo noi la copertura e ti richiamiamo con l'esito.
            </p>
            <form data-form={FONTE} onSubmit={submit} noValidate style={{ display: 'grid', gap: 10, textAlign: 'left' }}>
              <input style={rescueInputStyle} placeholder="Nome e cognome" autoComplete="name" value={nome} onChange={(e) => setNome(e.target.value)} />
              <input style={rescueInputStyle} type="tel" inputMode="tel" autoComplete="tel" placeholder="Cellulare o fisso" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: 'rgba(255,255,255,0.65)', cursor: 'pointer' }}>
                <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} style={{ marginTop: 2 }} />
                <span>Ho letto l'<a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>informativa privacy</a> e acconsento a essere ricontattato.</span>
              </label>
              {err && <p style={{ color: 'var(--sh-magenta)', fontSize: 13, margin: 0 }} role="alert">{err}</p>}
              <button className="cta-grad" type="submit" disabled={phase === 'sending'} style={{ justifyContent: 'center' }}>
                {phase === 'sending' ? 'Invio…' : <>Richiamatemi <Icons.arrow /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
